<template>
  <div class="trend-line-chart">
    <div v-if="loading" class="chart-loading">
      <el-icon class="is-loading"><i class="el-icon-loading"></i></el-icon>
      <span>数据加载中...</span>
    </div>
    <div v-else-if="!hasData" class="chart-empty">
      <i class="el-icon-s-marketing"></i>
      <span>暂无数据</span>
    </div>
    <div v-else ref="chart" class="chart-container"></div>
  </div>
</template>

<script>
import echarts from 'echarts'

export default {
  name: 'trend-line-chart',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: 'times' // times 或 fee
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      chart: null,
      resizeHandler: null
    }
  },

  computed: {
    hasData() {
      return this.data && this.data.length > 0
    },

    chartData() {
      if (!this.hasData) return { dates: [], values: [] }

      const sortedData = [...this.data].sort((a, b) => {
        return new Date(a.date) - new Date(b.date)
      })

      return {
        dates: sortedData.map(item => this.formatDate(item.date)),
        values: sortedData.map(item => this.type === 'times' ? (item.times || 0) : (item.fee || 0))
      }
    },

    yAxisName() {
      return this.type === 'times' ? '调用次数' : '费用金额 (¥)'
    },

    seriesName() {
      return this.type === 'times' ? '调用次数' : '费用'
    }
  },

  watch: {
    data: {
      handler() {
        this.$nextTick(() => {
          if (this.hasData && !this.chart) {
            this.initChart()
          } else if (this.chart) {
            this.renderChart()
          }
        })
      },
      deep: true
    },

    type() {
      if (this.chart) {
        this.renderChart()
      }
    },

    loading(newVal) {
      if (!newVal && this.hasData) {
        this.$nextTick(() => {
          if (!this.chart) {
            this.initChart()
          } else {
            this.renderChart()
          }
        })
      }
    }
  },

  mounted() {
    this.initChart()
    this.bindEvents()
  },

  beforeDestroy() {
    this.unbindEvents()
    if (this.chart) {
      this.chart.dispose()
      this.chart = null
    }
  },

  methods: {
    /**
     * 初始化图表
     */
    initChart() {
      if (!this.$refs.chart) return

      this.chart = echarts.init(this.$refs.chart)
      this.renderChart()
    },

    /**
     * 渲染图表
     */
    renderChart() {
      console.log('趋势图表渲染开始:', {
        hasChart: !!this.chart,
        hasData: this.hasData,
        dataLength: this.data ? this.data.length : 0,
        chartData: this.chartData,
        type: this.type
      })

      if (!this.chart || !this.hasData) {
        console.log('趋势图表渲染终止:', { hasChart: !!this.chart, hasData: this.hasData })
        return
      }

      const { dates, values } = this.chartData

      const option = {
        title: {
          text: `${this.seriesName}趋势`,
          left: 'center',
          top: 20,
          textStyle: {
            fontSize: 16,
            color: '#333'
          }
        },
        tooltip: {
          trigger: 'axis',
          formatter: (params) => {
            const param = params[0]
            const value = param.value
            const formattedValue = this.type === 'times'
              ? this.formatNumber(value)
              : '¥' + this.formatMoney(value)

            return `
              <div>
                <strong>${param.axisValue}</strong><br/>
                ${this.seriesName}: <span style="color: #409EFF">${formattedValue}</span>
              </div>
            `
          }
        },
        legend: {
          show: false
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '10%',
          top: '15%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: dates,
          axisLabel: {
            color: '#666',
            fontSize: 11,
            rotate: dates.length > 10 ? 45 : 0
          },
          axisLine: {
            lineStyle: {
              color: '#E4E7ED'
            }
          },
          axisTick: {
            show: false
          }
        },
        yAxis: {
          type: 'value',
          name: this.yAxisName,
          nameTextStyle: {
            color: '#666',
            fontSize: 12
          },
          axisLabel: {
            color: '#666',
            formatter: (value) => {
              if (this.type === 'times') {
                if (value >= 1000000) {
                  return (value / 1000000).toFixed(1) + 'M'
                } else if (value >= 1000) {
                  return (value / 1000).toFixed(1) + 'K'
                }
                return value.toString()
              } else {
                // fee类型
                if (value >= 10000) {
                  return (value / 10000).toFixed(1) + 'W'
                } else if (value >= 1000) {
                  return (value / 1000).toFixed(1) + 'K'
                }
                return value.toFixed(0)
              }
            }
          },
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          splitLine: {
            lineStyle: {
              color: '#E4E7ED',
              type: 'dashed'
            }
          }
        },
        series: [
          {
            name: this.seriesName,
            type: 'line',
            smooth: true,
            symbol: 'circle',
            symbolSize: 6,
            lineStyle: {
              width: 4,
              color: this.type === 'times'
                ? new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                    { offset: 0, color: '#667eea' },
                    { offset: 1, color: '#764ba2' }
                  ])
                : new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                    { offset: 0, color: '#f093fb' },
                    { offset: 1, color: '#f5576c' }
                  ]),
              shadowColor: this.type === 'times' ? 'rgba(102, 126, 234, 0.3)' : 'rgba(240, 147, 251, 0.3)',
              shadowBlur: 10,
              shadowOffsetY: 5
            },
            itemStyle: {
              color: this.type === 'times' ? '#667eea' : '#f093fb',
              borderWidth: 3,
              borderColor: '#fff',
              shadowColor: this.type === 'times' ? 'rgba(102, 126, 234, 0.4)' : 'rgba(240, 147, 251, 0.4)',
              shadowBlur: 8
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                {
                  offset: 0,
                  color: this.type === 'times'
                    ? 'rgba(102, 126, 234, 0.4)'
                    : 'rgba(240, 147, 251, 0.4)'
                },
                {
                  offset: 1,
                  color: this.type === 'times'
                    ? 'rgba(102, 126, 234, 0.05)'
                    : 'rgba(240, 147, 251, 0.05)'
                }
              ])
            },
            emphasis: {
              itemStyle: {
                color: this.type === 'times' ? '#7e8ff0' : '#f5a7be',
                borderWidth: 4,
                shadowColor: this.type === 'times' ? 'rgba(102, 126, 234, 0.6)' : 'rgba(240, 147, 251, 0.6)',
                shadowBlur: 15
              },
              scale: true,
              scaleSize: 10
            },
            data: values,
            animationDuration: 2000,
            animationEasing: 'cubicInOut'
          }
        ]
      }

      this.chart.setOption(option, true)
    },

    /**
     * 绑定事件
     */
    bindEvents() {
      this.resizeHandler = () => {
        if (this.chart) {
          this.chart.resize()
        }
      }
      window.addEventListener('resize', this.resizeHandler)
    },

    /**
     * 解绑事件
     */
    unbindEvents() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }
    },

    /**
     * 格式化日期
     * 如果数据跨年，则显示年月日；否则只显示月日
     */
    formatDate(dateStr) {
      if (!dateStr) return ''

      const date = new Date(dateStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      // 检查数据是否跨年
      const hasMultipleYears = this.checkMultipleYears()

      if (hasMultipleYears) {
        return `${year}-${month}-${day}`
      } else {
        return `${month}-${day}`
      }
    },

    /**
     * 检查数据是否跨年
     */
    checkMultipleYears() {
      if (!this.data || this.data.length === 0) return false

      const years = new Set()
      this.data.forEach(item => {
        if (item.date) {
          const year = new Date(item.date).getFullYear()
          years.add(year)
        }
      })

      return years.size > 1
    },

    /**
     * 格式化数字
     */
    formatNumber(num) {
      if (!num) return '0'
      return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    /**
     * 格式化金额
     */
    formatMoney(amount) {
      if (!amount) return '0.00'
      return Number(amount).toFixed(2)
    }
  }
}
</script>

<style scoped>
.trend-line-chart {
  height: 100%;
  position: relative;
  background: linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%);
  border-radius: 12px;
  padding: 8px;
}

.chart-container {
  width: 100%;
  height: 100%;
  min-height: 380px;
  animation: chartFadeIn 1s ease-out;
}

@keyframes chartFadeIn {
  from {
    opacity: 0;
    filter: blur(5px);
  }
  to {
    opacity: 1;
    filter: blur(0);
  }
}

.chart-loading,
.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 320px;
  color: #909399;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, rgba(250, 250, 250, 0.9) 100%);
  border-radius: 8px;
}

.chart-loading {
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
}

.chart-loading .el-icon {
  font-size: 32px;
  margin-bottom: 12px;
  color: #667eea;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.chart-empty i {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.3;
  color: #667eea;
  animation: wave 2s ease-in-out infinite;
}

@keyframes wave {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  25% {
    transform: translateY(-5px) rotate(-5deg);
  }
  75% {
    transform: translateY(-5px) rotate(5deg);
  }
}

.chart-empty span {
  font-size: 15px;
  color: #909399;
  font-weight: 500;
}
</style>



// WEBPACK FOOTER //
// src/custom/components/stats/message-stats/components/trend-line-chart.vue